import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = process.env.REACT_APP_INTERNAL_ADMIN_SERVER;

export interface CustomReportAlumniRow {
    student_id: string;
    name: string | null;
    program_name: string | null;
    pass_out_year: number | null;
    program_and_year: string;
    company_name: string | null;
    designation: string | null;
    layer: 'signal' | 'strategic' | 'activation' | null;
    alma_mater_pride: string | null;
    marketability: string | null;
    vibrancy: string | null;
    date_added: string | null;
    stage?: string | null;
    stage_label?: string | null;
    persisted_stage?: string | null;
    is_system_stage?: boolean;
}

export interface CustomReportWatchlistMeta {
    id: number;
    name: string;
    description: string | null;
    layer_context: string;
    tag: string;
}

export interface CustomReportResponse {
    watchlist: CustomReportWatchlistMeta;
    filters_applied: Record<string, any>;
    total_count: number;
    page: number;
    per_page: number;
    total_pages: number;
    alumni: CustomReportAlumniRow[];
}

export interface CustomReportFilters {
    institute_id: string | number;
    watchlist_id: string | number;
    filter_institute_id?: string | null;
    layer?: string | null;
    program?: string | null;
    batch_year?: string | null;
    alma_mater_pride?: string | null;
    marketability?: string | null;
    vibrancy?: string | null;
    date_from?: string | null;
    date_to?: string | null;
    search?: string | null;
    stage?: string | null;
    sort?: string | null;
    page?: string | number | null;
    per_page?: string | number | null;
}

const clean = (f: CustomReportFilters): Record<string, any> => {
    const out: Record<string, any> = {};
    Object.entries(f).forEach(([k, v]) => {
        if (v != null && v !== '') out[k] = v;
    });
    return out;
};

class CustomWatchlistReportService {
    static getReport(filters: CustomReportFilters): Promise<CustomReportResponse> {
        return axiosClient().get(`${BASE}/watchlists/custom/report`, { params: clean(filters) }).then((r) => r.data as CustomReportResponse);
    }

    static getFilterOptions(
        institute_id: string | number,
        watchlist_id: string | number,
        filter_institute_id?: string | null,
    ): Promise<{ programs: string[]; batch_years: number[] }> {
        const params: Record<string, any> = { institute_id, watchlist_id };
        if (filter_institute_id) params.filter_institute_id = filter_institute_id;
        return axiosClient()
            .get(`${BASE}/watchlists/custom/report/filter-options`, { params })
            .then((r) => r.data as { programs: string[]; batch_years: number[] });
    }

    static exportCSV(filters: CustomReportFilters): Promise<Blob> {
        return axiosClient()
            .get(`${BASE}/watchlists/custom/report/export`, { params: clean(filters), responseType: 'blob' })
            .then((r) => r.data as Blob);
    }

    static updateStage(
        watchlistId: string | number,
        studentId: string,
        body: { stage: string; remarks?: string | null },
        query: { institute_id: string | number; filter_institute_id?: string | null },
    ): Promise<{ success: boolean; student_id: string; stage: string; stage_label: string; remarks: string | null }> {
        const params: Record<string, any> = { institute_id: query.institute_id };
        if (query.filter_institute_id) params.filter_institute_id = query.filter_institute_id;
        const payload: Record<string, any> = { stage: body.stage };
        if (body.remarks != null && body.remarks !== '') payload.remarks = body.remarks;
        return axiosClient()
            .post(`${BASE}/watchlists/custom/${watchlistId}/members/${studentId}/stage`, payload, { params })
            .then((r) => r.data);
    }

    static removeMemberWithReason(
        watchlistId: string | number,
        studentId: string,
        body: { reason: string },
        query: { institute_id: string | number; filter_institute_id?: string | null },
    ): Promise<{ removed: number }> {
        const params: Record<string, any> = { institute_id: query.institute_id };
        if (query.filter_institute_id) params.filter_institute_id = query.filter_institute_id;
        return axiosClient()
            .post(`${BASE}/watchlists/custom/${watchlistId}/members/${studentId}/remove`, { reason: body.reason }, { params })
            .then((r) => r.data);
    }
}

export default CustomWatchlistReportService;
